import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { useDraggable } from '@dnd-kit/core';
import { Text } from '../../../text/Text';
import { TEXT } from '../../../itemIds';

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-start; /* left-align horizontally */
  padding: 8px;

  /* Show I-beam cursor when hovering over the editable text */
  [contenteditable='true'],
  [contenteditable='true'] * {
    cursor: text;
  }
`;

// Force inner content to span the full column width
const FullWidth = styled.div`
  width: 100%;
`;

// No custom textbox; we render the shared Text item so editing works identically

type SeqItemLite = {
  id: string | number;
  title?: string | null;
  body?: string | null;
  items?: SeqItemLite[];
};

type DndTextProps = {
  subItemId?: string | number; // legacy fallback
  subItem?: SeqItemLite; // direct payload
};

export const DndTextComponent = memo(({ subItemId, subItem }: DndTextProps) => {
  const { contentEditable } = useEditorStore();

  const draggableId = String(subItem?.id ?? subItemId ?? 'text');
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `text:${draggableId}`,
    disabled: !contentEditable
  });

  const TextComponent = useMemo(() => {
    const textItem = new Text({
      id: `${draggableId}_body`,
      type: TEXT,
      values: {
        value: (subItem?.body as string) || 'Default text',
        variant: 'text'
      },
      items: [],
      // Enable editing directly in the DnD canvas when in editor mode
      editorMode: contentEditable
    });
    return textItem.component();
  }, [draggableId, subItem?.body, contentEditable]);

  return (
    <Container
      ref={setNodeRef}
      {...(contentEditable ? { ...listeners, ...attributes } : {})}
      onPointerDownCapture={e => {
        if (!contentEditable) return;
        const target = e.target as HTMLElement;
        if (target && target.closest('[contenteditable="true"]')) {
          e.stopPropagation();
        }
      }}
      onMouseDownCapture={e => {
        if (!contentEditable) return;
        const target = e.target as HTMLElement;
        if (target && target.closest('[contenteditable="true"]')) {
          e.stopPropagation();
        }
      }}
      onTouchStartCapture={e => {
        if (!contentEditable) return;
        const target = e.target as HTMLElement;
        if (target && target.closest('[contenteditable="true"]')) {
          e.stopPropagation();
        }
      }}
      style={{
        opacity: isDragging ? 0.6 : 1,
        cursor: contentEditable
          ? isDragging
            ? 'grabbing'
            : 'grab'
          : 'default',
        touchAction: 'none'
      }}
      data-dnd-draggable={contentEditable ? 'true' : 'false'}
    >
      <FullWidth>
        <TextComponent />
      </FullWidth>
    </Container>
  );
});
